import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveCompanyWithSirenGuard, fillFromRegistry } from '../lib/companies';
import { TransferRequestsPanel } from '../components/TransferRequestsPanel';
import { SirenInlineInput } from '../components/SirenInlineInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { capitalizeFirst } from '../utils/capitalize';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
    Building2,
    Plus,
    Search,
    Filter,
    Eye,
    Pencil,
    Trash2,
    AlertTriangle,
    MapPin,
    Globe,
    User,
    Loader2,
    X,
    Upload,
    Download,
    FileSpreadsheet,
    CheckCircle2,
    XCircle,
    Users,
    Tag,
    Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';

// Colors for different users (same as Pipeline)
const USER_COLORS = [
    { bg: 'bg-blue-500', light: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-500' },
    { bg: 'bg-green-500', light: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-500' },
    { bg: 'bg-purple-500', light: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-500' },
    { bg: 'bg-orange-500', light: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-500' },
    { bg: 'bg-pink-500', light: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300', border: 'border-pink-500' },
    { bg: 'bg-cyan-500', light: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-500' },
    { bg: 'bg-yellow-500', light: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-500' },
    { bg: 'bg-red-500', light: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', border: 'border-red-500' },
];

const Companies = () => {
    const { api, user, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
    const [commercialFilter, setCommercialFilter] = useState(searchParams.get('commercial') || 'all');
    const [missingSirenFilter, setMissingSirenFilter] = useState(false);
    const [registryLoading, setRegistryLoading] = useState(false);
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Import state
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    
    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [showBulkActionModal, setShowBulkActionModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('');
    const [bulkActionValue, setBulkActionValue] = useState('');
    const [bulkActionStatuses, setBulkActionStatuses] = useState([]);
    const [users, setUsers] = useState([]);
    const [userColorMap, setUserColorMap] = useState({});
    
    // Form state
    const [formData, setFormData] = useState({
        legal_name: '',
        trade_name: '',
        siret: '',
        website: '',
        address_line_1: '',
        postal_code: '',
        city: '',
        sector: '',
        source: '',
        notes: '',
        statuses: ['prospect'],  // Changed to array for checkboxes
        owner_user_id: '',  // Commercial affilié
    });

    // Status options for checkboxes
    const statusOptions = [
        { value: 'prospect', label: 'Prospect' },
        { value: 'client', label: 'Client' },
        { value: 'annonceur_mag_immo', label: "Annonceur Mag' de l'Immo" },
        { value: 'annonceur_place_to_b', label: 'Annonceur Place to B' },
        { value: 'annonceur_decideur', label: 'Annonceur Décideur' },
        { value: 'inactive', label: 'Inactif' },
    ];

    useEffect(() => {
        fetchCompanies();
        fetchUsers();
    }, [searchQuery, statusFilter, commercialFilter, missingSirenFilter]);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users/directory');
            const filteredUsers = response.data.filter(u => u.role === 'commercial' || u.role === 'super_admin');
            setUsers(filteredUsers);
            
            // Create user color map
            const colorMap = {};
            filteredUsers.forEach((u, index) => {
                colorMap[u.id] = USER_COLORS[index % USER_COLORS.length];
            });
            setUserColorMap(colorMap);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    };

    // Handle edit parameter from URL
    useEffect(() => {
        const editId = searchParams.get('edit');
        if (editId && companies.length > 0) {
            const companyToEdit = companies.find(c => c.id === editId);
            if (companyToEdit) {
                openEditModal(companyToEdit);
                // Clear the edit parameter from URL
                setSearchParams(prev => {
                    prev.delete('edit');
                    return prev;
                });
            }
        }
    }, [searchParams, companies]);

    const fetchCompanies = async (retryCount = 0) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchQuery) params.append('search', searchQuery);
            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
            if (commercialFilter && commercialFilter !== 'all') params.append('owner_user_id', commercialFilter);
            if (missingSirenFilter) params.append('missing_siren', 'true');
            
            const response = await api.get(`/companies?${params.toString()}`);
            setCompanies(response.data);
        } catch (error) {
            console.error('Failed to fetch companies:', error);
            // Retry once on failure (e.g., token refresh issue)
            if (retryCount < 1 && error.response?.status === 401) {
                console.log('Retrying after auth error...');
                setTimeout(() => fetchCompanies(retryCount + 1), 1000);
                return;
            }
            toast.error('Erreur lors du chargement des entreprises');
        } finally {
            setLoading(false);
        }
    };

    const checkDuplicates = async () => {
        if (!formData.legal_name && !formData.siret) return false;
        
        try {
            const params = new URLSearchParams();
            if (formData.legal_name) params.append('legal_name', formData.legal_name);
            if (formData.siret) params.append('siret', formData.siret);
            // Exclure l'entreprise en cours de modification
            if (selectedCompany?.id) params.append('exclude_id', selectedCompany.id);
            
            const response = await api.get(`/companies/check-duplicates?${params.toString()}`);
            
            if (response.data.has_duplicates) {
                setDuplicateWarning(response.data);
                return !response.data.duplicates.some((d) => d.match_type === 'siret');
            }
        } catch (error) {
            console.error('Failed to check duplicates:', error);
        }
        
        setDuplicateWarning(null);
        return false;
    };

    const autofillFromRegistry = async (siret) => {
        setRegistryLoading(true);
        try {
            const { pickEmpty, isActive } = await fillFromRegistry(api, siret, sectors);
            const filled = pickEmpty(formData);
            setFormData((prev) => ({ ...prev, ...pickEmpty(prev) }));
            const count = Object.keys(filled).length;
            if (count > 0) toast.success(`${count} champ(s) complété(s) depuis le registre officiel`);
            if (!isActive) toast.warning('Attention : cette entreprise est fermée selon le registre officiel');
        } catch (error) {
            toast.info(error.response?.data?.detail || 'Registre officiel indisponible');
        } finally {
            setRegistryLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        // Check duplicates first
        const hasDuplicates = await checkDuplicates();
        if (hasDuplicates && !window.confirm('Des entreprises similaires existent déjà. Voulez-vous continuer ?')) {
            setSubmitting(false);
            return;
        }
        
        try {
            const response = await saveCompanyWithSirenGuard(
                (force) => api.post(`/companies${force ? '?force=true' : ''}`, formData),
                { api, canForce: isSuperAdmin() }
            );
            toast.success('Entreprise créée avec succès');
            setShowCreateModal(false);
            resetForm();
            fetchCompanies();
            navigate(`/companies/${response.data.id}`);
        } catch (error) {
            console.error('Failed to create company:', error);
            if (!error.handled) toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await saveCompanyWithSirenGuard(
                (force) => api.patch(`/companies/${selectedCompany.id}${force ? '?force=true' : ''}`, formData),
                { api, canForce: isSuperAdmin() }
            );
            toast.success('Entreprise mise à jour');
            setShowEditModal(false);
            fetchCompanies();
        } catch (error) {
            console.error('Failed to update company:', error);
            if (!error.handled) toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        
        try {
            await api.delete(`/companies/${selectedCompany.id}`);
            toast.success('Entreprise supprimée');
            setShowDeleteModal(false);
            fetchCompanies();
        } catch (error) {
            console.error('Failed to delete company:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            legal_name: '',
            trade_name: '',
            siret: '',
            website: '',
            address_line_1: '',
            postal_code: '',
            city: '',
            sector: '',
            source: '',
            notes: '',
            statuses: ['prospect'],
        });
        setDuplicateWarning(null);
    };

    // Import functions
    const handleImport = async () => {
        if (!importFile) {
            toast.error('Veuillez sélectionner un fichier');
            return;
        }

        setImporting(true);
        setImportResult(null);

        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('skip_duplicates', skipDuplicates);

            const response = await api.post('/companies/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setImportResult(response.data);
            
            if (response.data.imported > 0) {
                toast.success(`${response.data.imported} entreprise(s) importée(s) avec succès`);
                fetchCompanies();
            }
            
            if (response.data.skipped > 0) {
                toast.info(`${response.data.skipped} doublon(s) ignoré(s)`);
            }
            
            if (response.data.errors.length > 0) {
                toast.warning(`${response.data.errors.length} erreur(s) lors de l'import`);
            }
        } catch (error) {
            console.error('Import failed:', error);
            toast.error(error.response?.data?.detail || "Erreur lors de l'import");
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = async (format) => {
        try {
            const response = await api.get(`/companies/import/template?format=${format}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `modele_import_entreprises.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Modèle téléchargé');
        } catch (error) {
            console.error('Download failed:', error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const resetImportModal = () => {
        setImportFile(null);
        setImportResult(null);
        setSkipDuplicates(true);
        setShowImportModal(false);
    };

    // Selection functions
    const toggleSelection = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === companies.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(companies.map(c => c.id));
        }
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    const openBulkActionModal = (action) => {
        setBulkAction(action);
        setBulkActionValue('');
        setBulkActionStatuses([]);
        setShowBulkActionModal(true);
    };

    const handleBulkAction = async () => {
        if (selectedIds.length === 0) return;
        
        setSubmitting(true);
        try {
            const payload = {
                company_ids: selectedIds,
                action: bulkAction,
                value: bulkActionValue || null,
                statuses: bulkActionStatuses.length > 0 ? bulkActionStatuses : null,
            };
            
            const response = await api.post('/companies/bulk-action', payload);
            
            if (response.data.success > 0) {
                toast.success(`${response.data.success} entreprise(s) modifiée(s)`);
                fetchCompanies();
                clearSelection();
            }
            if (response.data.failed > 0) {
                toast.error(`${response.data.failed} erreur(s)`);
            }
            
            setShowBulkActionModal(false);
        } catch (error) {
            console.error('Bulk action failed:', error);
            toast.error(error.response?.data?.detail || "Erreur lors de l'action");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleBulkStatus = (status) => {
        setBulkActionStatuses(prev => 
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        );
    };

    const openEditModal = (company) => {
        setSelectedCompany(company);
        setFormData({
            legal_name: company.legal_name || '',
            trade_name: company.trade_name || '',
            siret: company.siret || '',
            website: company.website || '',
            address_line_1: company.address_line_1 || '',
            postal_code: company.postal_code || '',
            city: company.city || '',
            sector: company.sector || '',
            source: company.source || '',
            notes: company.notes || '',
            statuses: company.statuses || (company.status ? [company.status] : ['prospect']),
        });
        setShowEditModal(true);
    };

    const getStatusBadge = (statuses) => {
        // Handle both array (new) and string (legacy) formats
        const statusList = Array.isArray(statuses) ? statuses : (statuses ? [statuses] : ['prospect']);
        
        const styles = {
            prospect: 'bg-blue-500/10 text-blue-500',
            client: 'bg-green-500/10 text-green-500',
            annonceur_mag_immo: 'bg-purple-500/10 text-purple-500',
            annonceur_place_to_b: 'bg-orange-500/10 text-orange-500',
            annonceur_decideur: 'bg-pink-500/10 text-pink-500',
            inactive: 'bg-gray-500/10 text-gray-500',
        };
        const labels = {
            prospect: 'Prospect',
            client: 'Client',
            annonceur_mag_immo: "Mag' de l'Immo",
            annonceur_place_to_b: 'Place to B',
            annonceur_decideur: 'Décideur',
            inactive: 'Inactif',
        };
        
        return (
            <div className="flex flex-wrap gap-1">
                {statusList.map((status) => (
                    <Badge key={status} className={styles[status] || styles.prospect}>
                        {labels[status] || status}
                    </Badge>
                ))}
            </div>
        );
    };

    const toggleStatus = (statusValue) => {
        setFormData(prev => {
            const currentStatuses = prev.statuses || [];
            if (currentStatuses.includes(statusValue)) {
                // Remove status, but ensure at least one remains
                const newStatuses = currentStatuses.filter(s => s !== statusValue);
                return { ...prev, statuses: newStatuses.length > 0 ? newStatuses : currentStatuses };
            } else {
                // Add status
                return { ...prev, statuses: [...currentStatuses, statusValue] };
            }
        });
    };

    const sectors = [
        'Technologie', 'Finance', 'Santé', 'Immobilier', 'Commerce', 
        'Industrie', 'Services', 'Médias', 'Tourisme', 'Autre'
    ];

    const sources = [
        'Site web', 'Recommandation', 'Réseau', 'Événement', 
        'Publicité', 'Prospection', 'Autre'
    ];

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground">Entreprises</h1>
                    <p className="text-muted-foreground mt-2 text-sm md:text-base">
                        Gérez votre base clients et prospects
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        onClick={() => setShowImportModal(true)}
                        className="rounded-full border-border/40 hover:border-primary/50"
                        data-testid="import-companies-btn"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                    </Button>
                    <Button
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="rounded-full"
                        data-testid="create-company-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle entreprise
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher par nom, SIRET/SIREN, ville..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 rounded-lg border-border/40 focus:border-primary/50"
                                data-testid="company-search-input"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-48 rounded-lg border-border/40" data-testid="status-filter">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                <SelectItem value="prospect">Prospects</SelectItem>
                                <SelectItem value="client">Clients</SelectItem>
                                <SelectItem value="inactive">Inactifs</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={commercialFilter} onValueChange={setCommercialFilter}>
                            <SelectTrigger className="w-full sm:w-52 rounded-lg border-border/40" data-testid="commercial-filter">
                                <Users className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Commercial" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les commerciaux</SelectItem>
                                <SelectItem value="unassigned">Non assigné</SelectItem>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant={missingSirenFilter ? 'default' : 'outline'}
                            onClick={() => setMissingSirenFilter((v) => !v)}
                            className="rounded-lg border-border/40"
                            data-testid="missing-siren-filter"
                        >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Sans SIREN
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <TransferRequestsPanel api={api} onChanged={fetchCompanies} />

            {/* Selection Actions Bar */}
            {selectedIds.length > 0 && (
                <Card className="border-primary/50 bg-primary/5">
                    <CardContent className="py-3 px-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <span className="font-medium">
                                    {selectedIds.length} entreprise(s) sélectionnée(s)
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearSelection}
                                    className="rounded-full"
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Désélectionner
                                </Button>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openBulkActionModal('assign_owner')}
                                    className="rounded-full"
                                >
                                    <Users className="w-4 h-4 mr-2" />
                                    Assigner
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openBulkActionModal('change_status')}
                                    className="rounded-full"
                                >
                                    <Tag className="w-4 h-4 mr-2" />
                                    Statut
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openBulkActionModal('change_sector')}
                                    className="rounded-full"
                                >
                                    <Briefcase className="w-4 h-4 mr-2" />
                                    Secteur
                                </Button>
                                {isSuperAdmin() && (
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => openBulkActionModal('delete')}
                                        className="rounded-full"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Supprimer
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 space-y-4">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-16 w-full" />
                            ))}
                        </div>
                    ) : companies.length === 0 ? (
                        <div className="text-center py-12">
                            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">Aucune entreprise trouvée</p>
                            <Button
                                variant="link"
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="mt-2"
                            >
                                Créer une entreprise
                            </Button>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[60px] pl-6">
                                        <Checkbox
                                            checked={selectedIds.length === companies.length && companies.length > 0}
                                            onCheckedChange={toggleSelectAll}
                                            aria-label="Sélectionner tout"
                                        />
                                    </TableHead>
                                    <TableHead>Entreprise</TableHead>
                                    {missingSirenFilter && <TableHead>SIREN à compléter</TableHead>}
                                    <TableHead className="hidden md:table-cell">Ville</TableHead>
                                    <TableHead className="hidden lg:table-cell">Secteur</TableHead>
                                    <TableHead className="hidden lg:table-cell">Commercial</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="w-[120px] pr-6"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {companies.map((company) => (
                                    <TableRow
                                        key={company.id}
                                        className={`cursor-pointer ${selectedIds.includes(company.id) ? 'bg-primary/5' : ''}`}
                                        data-testid={`company-row-${company.id}`}
                                    >
                                        <TableCell className="pl-6" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedIds.includes(company.id)}
                                                onCheckedChange={() => toggleSelection(company.id)}
                                                aria-label={`Sélectionner ${company.legal_name}`}
                                            />
                                        </TableCell>
                                        <TableCell onClick={() => navigate(`/companies/${company.id}`)}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <Building2 className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{company.legal_name}</p>
                                                    {company.trade_name && (
                                                        <p className="text-sm text-muted-foreground">{company.trade_name}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        {missingSirenFilter && (
                                            <TableCell>
                                                <SirenInlineInput api={api} company={company} canForce={isSuperAdmin()} onSaved={fetchCompanies} />
                                            </TableCell>
                                        )}
                                        <TableCell className="hidden md:table-cell">
                                            {company.city ? (
                                                <div className="flex items-center gap-1 text-muted-foreground">
                                                    <MapPin className="w-4 h-4" />
                                                    {company.city}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell">
                                            {company.sector || <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell">
                                            {company.owner_name ? (
                                                <div className="flex items-center gap-2">
                                                    <span 
                                                        className={`w-3 h-3 rounded-full ${userColorMap[company.owner_user_id]?.bg || 'bg-gray-400'}`}
                                                    />
                                                    <span className={userColorMap[company.owner_user_id]?.text || ''}>
                                                        {company.owner_name}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(company.statuses || company.status)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="rounded-full h-8 w-8"
                                                    onClick={() => navigate(`/companies/${company.id}`)}
                                                    title="Voir"
                                                >
                                                    <Eye className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="rounded-full h-8 w-8"
                                                    onClick={() => openEditModal(company)}
                                                    title="Modifier"
                                                >
                                                    <Pencil className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                </Button>
                                                {isSuperAdmin() && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="rounded-full h-8 w-8"
                                                        onClick={() => {
                                                            setSelectedCompany(company);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                }
            }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {showEditModal ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
                        </DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Mettez à jour les informations' : 'Renseignez les informations de l\'entreprise'}
                        </DialogDescription>
                    </DialogHeader>

                    {duplicateWarning && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertTriangle className="w-4 h-4" />
                            <AlertDescription>
                                <p className="font-medium">{duplicateWarning.message}</p>
                                <ul className="mt-2 text-sm">
                                    {duplicateWarning.duplicates.map((d) => (
                                        <li key={d.id}>
                                            • {d.legal_name} ({d.match_type === 'siret' ? 'SIRET/SIREN identique' : 'Nom similaire'})
                                        </li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="legal_name">Raison sociale *</Label>
                                <Input
                                    id="legal_name"
                                    value={formData.legal_name}
                                    onChange={(e) => setFormData({ ...formData, legal_name: capitalizeFirst(e.target.value) })}
                                    onBlur={checkDuplicates}
                                    required
                                    className="rounded-lg"
                                    data-testid="company-legal-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="trade_name">Nom commercial</Label>
                                <Input
                                    id="trade_name"
                                    value={formData.trade_name}
                                    onChange={(e) => setFormData({ ...formData, trade_name: capitalizeFirst(e.target.value) })}
                                    className="rounded-lg"
                                    data-testid="company-trade-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="siret">SIRET / SIREN *</Label>
                                <Input
                                    id="siret"
                                    value={formData.siret}
                                    onChange={(e) => {
                                        // Only allow digits, spaces and dashes
                                        const value = e.target.value.replace(/[^\d\s\-]/g, '');
                                        setFormData({ ...formData, siret: value });
                                    }}
                                    onBlur={(e) => {
                                        // Clean and validate on blur
                                        const cleaned = e.target.value.replace(/[\s\-]/g, '');
                                        if (cleaned && cleaned.length !== 9 && cleaned.length !== 14) {
                                            toast.error('Le numéro doit être un SIREN (9 chiffres) ou un SIRET (14 chiffres)');
                                        }
                                        setFormData({ ...formData, siret: cleaned });
                                        checkDuplicates();
                                        if (cleaned.length === 9 || cleaned.length === 14) autofillFromRegistry(cleaned);
                                    }}
                                    placeholder="9 ou 14 chiffres"
                                    required
                                    className="rounded-lg"
                                    data-testid="company-siret"
                                />
                                <p className="text-xs text-muted-foreground">
                                    {registryLoading ? 'Recherche dans le registre officiel…' : 'Obligatoire — SIREN : 9 chiffres • SIRET : 14 chiffres'}
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="website">Site web</Label>
                                <Input
                                    id="website"
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                    placeholder="https://"
                                    className="rounded-lg"
                                    data-testid="company-website"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="address">Adresse</Label>
                                <Input
                                    id="address"
                                    value={formData.address_line_1}
                                    onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                                    className="rounded-lg"
                                    data-testid="company-address"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="postal_code">Code postal</Label>
                                <Input
                                    id="postal_code"
                                    value={formData.postal_code}
                                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                    className="rounded-lg"
                                    data-testid="company-postal-code"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="city">Ville</Label>
                                <Input
                                    id="city"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: capitalizeFirst(e.target.value) })}
                                    className="rounded-lg"
                                    data-testid="company-city"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="sector">Secteur d'activité</Label>
                                <Select
                                    value={formData.sector}
                                    onValueChange={(value) => setFormData({ ...formData, sector: value })}
                                >
                                    <SelectTrigger className="rounded-lg" data-testid="company-sector">
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {sectors.map((sector) => (
                                            <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="source">Source du lead</Label>
                                <Select
                                    value={formData.source}
                                    onValueChange={(value) => setFormData({ ...formData, source: value })}
                                >
                                    <SelectTrigger className="rounded-lg" data-testid="company-source">
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {sources.map((source) => (
                                            <SelectItem key={source} value={source}>{source}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Statuts</Label>
                                <div className="grid grid-cols-2 gap-3 p-3 border rounded-lg bg-muted/30">
                                    {statusOptions.map((option) => (
                                        <div key={option.value} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`status-${option.value}`}
                                                checked={(formData.statuses || []).includes(option.value)}
                                                onCheckedChange={() => toggleStatus(option.value)}
                                                data-testid={`status-checkbox-${option.value}`}
                                            />
                                            <label
                                                htmlFor={`status-${option.value}`}
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                            >
                                                {option.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Commercial affilié</Label>
                                <Select
                                    value={formData.owner_user_id || ''}
                                    onValueChange={(value) => setFormData({ ...formData, owner_user_id: value })}
                                    disabled={showEditModal && !isSuperAdmin()}
                                >
                                    <SelectTrigger className="rounded-lg" data-testid="company-owner">
                                        <SelectValue placeholder="Sélectionner un commercial" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {users.map((u) => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {showEditModal && !isSuperAdmin() && (
                                    <p className="text-xs text-muted-foreground">
                                        Seul un Super Admin peut modifier le commercial affilié
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="notes">Notes internes</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={3}
                                    className="rounded-lg"
                                    data-testid="company-notes"
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="rounded-full"
                                data-testid="company-submit-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {showEditModal ? 'Mise à jour...' : 'Création...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Mettre à jour' : 'Créer'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr de vouloir supprimer l'entreprise "{selectedCompany?.legal_name}" ?
                            Cette action supprimera également tous les contacts, opportunités et activités associés.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={submitting}
                            className="rounded-full"
                            data-testid="confirm-delete-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Suppression...
                                </>
                            ) : (
                                'Supprimer'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Modal */}
            <Dialog open={showImportModal} onOpenChange={(open) => !open && resetImportModal()}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5" />
                            Importer des entreprises
                        </DialogTitle>
                        <DialogDescription>
                            Importez vos entreprises depuis un fichier CSV ou Excel (XLS/XLSX)
                        </DialogDescription>
                    </DialogHeader>

                    {!importResult ? (
                        <div className="space-y-4">
                            {/* Download template */}
                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2 text-sm">
                                    <Download className="w-4 h-4 text-muted-foreground" />
                                    <span>Télécharger le modèle</span>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadTemplate('csv')}
                                        className="rounded-full"
                                    >
                                        CSV
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadTemplate('xlsx')}
                                        className="rounded-full"
                                    >
                                        Excel
                                    </Button>
                                </div>
                            </div>

                            {/* File upload */}
                            <div className="space-y-2">
                                <Label>Fichier à importer</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        accept=".csv,.xls,.xlsx"
                                        onChange={(e) => setImportFile(e.target.files[0])}
                                        className="rounded-lg"
                                        data-testid="import-file-input"
                                    />
                                </div>
                                {importFile && (
                                    <p className="text-sm text-muted-foreground">
                                        Fichier sélectionné: {importFile.name}
                                    </p>
                                )}
                            </div>

                            {/* Options */}
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skip-duplicates"
                                    checked={skipDuplicates}
                                    onCheckedChange={setSkipDuplicates}
                                />
                                <label
                                    htmlFor="skip-duplicates"
                                    className="text-sm font-medium leading-none cursor-pointer"
                                >
                                    Ignorer les doublons (même nom ou SIRET/SIREN)
                                </label>
                            </div>

                            {/* Column info */}
                            <Alert>
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                    <strong>Colonnes reconnues :</strong> raison_sociale (obligatoire), siret/siren (obligatoire), nom_commercial, site_web, adresse, code_postal, ville, secteur, source, notes, statut
                                </AlertDescription>
                            </Alert>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Results summary */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center p-3 bg-green-500/10 rounded-lg">
                                    <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-1" />
                                    <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                                    <div className="text-xs text-muted-foreground">Importées</div>
                                </div>
                                <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
                                    <AlertTriangle className="w-6 h-6 mx-auto text-yellow-500 mb-1" />
                                    <div className="text-2xl font-bold text-yellow-600">{importResult.skipped}</div>
                                    <div className="text-xs text-muted-foreground">Doublons</div>
                                </div>
                                <div className="text-center p-3 bg-red-500/10 rounded-lg">
                                    <XCircle className="w-6 h-6 mx-auto text-red-500 mb-1" />
                                    <div className="text-2xl font-bold text-red-600">{importResult.errors.length}</div>
                                    <div className="text-xs text-muted-foreground">Erreurs</div>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span>Progression</span>
                                    <span>{importResult.imported + importResult.skipped + importResult.errors.length} / {importResult.total_rows}</span>
                                </div>
                                <Progress 
                                    value={((importResult.imported + importResult.skipped + importResult.errors.length) / importResult.total_rows) * 100} 
                                    className="h-2"
                                />
                            </div>

                            {/* Duplicates list */}
                            {importResult.duplicates.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-yellow-600">Doublons ignorés :</Label>
                                    <div className="max-h-24 overflow-y-auto text-sm text-muted-foreground bg-muted/30 p-2 rounded">
                                        {importResult.duplicates.slice(0, 10).join(', ')}
                                        {importResult.duplicates.length > 10 && ` et ${importResult.duplicates.length - 10} autres...`}
                                    </div>
                                </div>
                            )}

                            {/* Errors list */}
                            {importResult.errors.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-red-600">Erreurs :</Label>
                                    <div className="max-h-32 overflow-y-auto text-sm bg-red-500/5 p-2 rounded space-y-1">
                                        {importResult.errors.slice(0, 10).map((err, idx) => (
                                            <div key={idx} className="text-red-600">
                                                Ligne {err.row}: {err.error}
                                            </div>
                                        ))}
                                        {importResult.errors.length > 10 && (
                                            <div className="text-muted-foreground">
                                                ... et {importResult.errors.length - 10} autres erreurs
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        {!importResult ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={resetImportModal}
                                    className="rounded-full"
                                >
                                    Annuler
                                </Button>
                                <Button
                                    onClick={handleImport}
                                    disabled={!importFile || importing}
                                    className="rounded-full"
                                    data-testid="confirm-import-btn"
                                >
                                    {importing ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Import en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Importer
                                        </>
                                    )}
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={resetImportModal}
                                className="rounded-full"
                            >
                                Fermer
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Action Modal */}
            <Dialog open={showBulkActionModal} onOpenChange={setShowBulkActionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {bulkAction === 'delete' && 'Supprimer les entreprises sélectionnées'}
                            {bulkAction === 'assign_owner' && 'Assigner à un commercial'}
                            {bulkAction === 'change_status' && 'Changer le statut'}
                            {bulkAction === 'change_sector' && 'Changer le secteur'}
                        </DialogTitle>
                        <DialogDescription>
                            Cette action sera appliquée à {selectedIds.length} entreprise(s)
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {bulkAction === 'delete' && (
                            <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    Cette action supprimera définitivement les {selectedIds.length} entreprises sélectionnées ainsi que tous leurs contacts, opportunités et activités associés.
                                </AlertDescription>
                            </Alert>
                        )}

                        {bulkAction === 'assign_owner' && (
                            <div className="space-y-2">
                                <Label>Commercial</Label>
                                <Select value={bulkActionValue} onValueChange={setBulkActionValue}>
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue placeholder="Sélectionner un commercial" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {users.map((user) => (
                                            <SelectItem key={user.id} value={user.id}>
                                                {user.first_name} {user.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {bulkAction === 'change_status' && (
                            <div className="space-y-2">
                                <Label>Statuts</Label>
                                <div className="grid grid-cols-2 gap-3 p-3 border rounded-lg bg-muted/30">
                                    {statusOptions.map((option) => (
                                        <div key={option.value} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={`bulk-status-${option.value}`}
                                                checked={bulkActionStatuses.includes(option.value)}
                                                onCheckedChange={() => toggleBulkStatus(option.value)}
                                            />
                                            <label
                                                htmlFor={`bulk-status-${option.value}`}
                                                className="text-sm font-medium leading-none cursor-pointer"
                                            >
                                                {option.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {bulkAction === 'change_sector' && (
                            <div className="space-y-2">
                                <Label>Secteur</Label>
                                <Input
                                    value={bulkActionValue}
                                    onChange={(e) => setBulkActionValue(e.target.value)}
                                    placeholder="Ex: Immobilier, Finance, Médias..."
                                    className="rounded-lg"
                                />
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowBulkActionModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            variant={bulkAction === 'delete' ? 'destructive' : 'default'}
                            onClick={handleBulkAction}
                            disabled={submitting || (bulkAction === 'assign_owner' && !bulkActionValue) || (bulkAction === 'change_status' && bulkActionStatuses.length === 0) || (bulkAction === 'change_sector' && !bulkActionValue)}
                            className="rounded-full"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                bulkAction === 'delete' ? 'Supprimer' : 'Appliquer'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Companies;
